<template>
  <v-container
    fluid
    class="rfid-page pa-4"
  >
    <!-- ─── PAGE HEADER ──────────────────────────────────────────────── -->
    <div class="rfid-page__header mb-4">
      <div class="d-flex align-center justify-space-between flex-wrap">
        <div>
          <div class="d-flex align-center">
            <v-icon
              color="teal"
              class="mr-2"
            >
              mdi-credit-card-wireless
            </v-icon>
            <span class="rfid-page__title">RFID Management</span>
            <v-chip
              small
              color="teal"
              dark
              class="ml-3 rfid-page__wbs-badge"
            >
              WBS 1.3.2
            </v-chip>
          </div>
          <p class="rfid-page__subtitle mt-1 mb-0">
            Register RFID cards, allocate to chargers and configure
            authorization modes — ISO/IEC 14443-A/B · ISO/IEC 18092 · IEC/ISO
            15693
          </p>
        </div>
        <div class="d-flex align-center mt-2 mt-sm-0">
          <v-btn
            outlined
            color="teal"
            class="mr-2"
            @click="openImportDialog"
          >
            <v-icon
              left
              small
            >
              mdi-upload
            </v-icon>
            CSV Import
          </v-btn>
          <v-btn
            color="teal"
            dark
            @click="openAddDialog"
          >
            <v-icon
              left
              small
            >
              mdi-plus
            </v-icon>
            Add RFID Card
          </v-btn>
        </div>
      </div>
    </div>

    <!-- ─── KPI SUMMARY CARDS ─────────────────────────────────────────── -->
    <v-row class="mb-4">
      <v-col
        v-for="kpi in kpiCards"
        :key="kpi.label"
        cols="6"
        sm="4"
        md="2"
      >
        <v-card
          class="rfid-kpi-card text-center pa-3"
          outlined
        >
          <v-icon
            :color="kpi.color"
            size="28"
          >
            {{ kpi.icon }}
          </v-icon>
          <div
            class="rfid-kpi-card__value mt-1"
            :style="{ color: kpi.color }"
          >
            {{ kpi.value }}
          </div>
          <div class="rfid-kpi-card__label">
            {{ kpi.label }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- ─── FILTERS ───────────────────────────────────────────────────── -->
    <v-card
      outlined
      class="mb-4 pa-3"
    >
      <v-row
        dense
        align="center"
      >
        <v-col
          cols="12"
          sm="4"
          md="3"
        >
          <v-text-field
            v-model="filters.search"
            placeholder="Search UID, name, charger…"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            color="teal"
          />
        </v-col>
        <v-col
          cols="6"
          sm="4"
          md="2"
        >
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            label="Status"
            outlined
            dense
            hide-details
            clearable
            color="teal"
          />
        </v-col>
        <v-col
          cols="6"
          sm="4"
          md="2"
        >
          <v-select
            v-model="filters.authMode"
            :items="authModeOptions"
            label="Auth Mode"
            outlined
            dense
            hide-details
            clearable
            color="teal"
          />
        </v-col>
        <v-col
          cols="6"
          sm="4"
          md="2"
        >
          <v-select
            v-model="filters.tenant"
            :items="tenantOptions"
            label="Tenant"
            outlined
            dense
            hide-details
            clearable
            color="teal"
          />
        </v-col>
        <v-col
          cols="6"
          sm="4"
          md="2"
        >
          <v-select
            v-model="filters.allocated"
            :items="[
              { text: 'All', value: null },
              { text: 'Allocated', value: true },
              { text: 'Unallocated', value: false },
            ]"
            label="Allocation"
            outlined
            dense
            hide-details
            color="teal"
          />
        </v-col>
        <v-col
          cols="12"
          sm="4"
          md="1"
          class="text-right"
        >
          <v-btn
            text
            color="teal"
            small
            @click="clearFilters"
          >
            Clear
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- ─── MAIN TABLE ────────────────────────────────────────────────── -->
    <v-card outlined>
      <v-data-table
        :headers="tableHeaders"
        :items="filteredCards"
        :loading="loading"
        :items-per-page="10"
        :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
        class="rfid-table"
        loading-text="Loading RFID cards…"
        no-data-text="No RFID cards found. Add your first card to get started."
      >
        <!-- Card UID -->
        <template #item.cardUid="{ item }">
          <div class="d-flex align-center">
            <v-icon
              small
              color="teal"
              class="mr-1"
            >
              mdi-credit-card-wireless
            </v-icon>
            <span class="rfid-table__uid font-weight-medium">{{
              item.cardUid
            }}</span>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn
                  icon
                  x-small
                  color="grey"
                  class="ml-1"
                  v-on="on"
                  @click.stop="copyToClipboard(item.cardUid)"
                >
                  <v-icon x-small>
                    mdi-content-copy
                  </v-icon>
                </v-btn>
              </template>
              <span>Copy UID</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Card Type -->
        <template #item.cardType="{ item }">
          <v-chip
            x-small
            :color="cardTypeColor(item.cardType).bg"
            :text-color="cardTypeColor(item.cardType).text"
            class="font-weight-medium"
          >
            {{ item.cardType }}
          </v-chip>
        </template>

        <!-- Assigned To -->
        <template #item.assignedTo="{ item }">
          <div class="d-flex align-center">
            <v-avatar
              size="24"
              color="teal lighten-4"
              class="mr-2"
            >
              <span class="teal--text text--darken-2 caption font-weight-bold">
                {{ item.assignedTo ? item.assignedTo.charAt(0) : "?" }}
              </span>
            </v-avatar>
            <span class="body-2">{{ item.assignedTo || "—" }}</span>
          </div>
        </template>

        <!-- Allocated Charger -->
        <template #item.allocatedCharger="{ item }">
          <div v-if="item.allocatedCharger">
            <div class="d-flex align-center">
              <v-icon
                x-small
                color="teal"
                class="mr-1"
              >
                mdi-ev-station
              </v-icon>
              <span class="body-2 font-weight-medium teal--text">
                {{ item.allocatedCharger }}
              </span>
            </div>
            <div class="caption grey--text">
              {{ item.allocatedSite }}
            </div>
          </div>
          <v-chip
            v-else
            x-small
            outlined
            color="grey"
          >
            Unallocated
          </v-chip>
        </template>

        <!-- Auth Mode -->
        <template #item.authMode="{ item }">
          <div v-if="item.authMode">
            <v-chip
              x-small
              :color="authModeColor(item.authMode).bg"
              :text-color="authModeColor(item.authMode).text"
              class="font-weight-medium"
            >
              <v-icon
                x-small
                left
              >
                {{ authModeIcon(item.authMode) }}
              </v-icon>
              {{ item.authMode }}
            </v-chip>
          </div>
          <span
            v-else
            class="caption grey--text"
          >Not set</span>
        </template>

        <!-- Status -->
        <template #item.status="{ item }">
          <v-chip
            x-small
            :color="statusColor(item.status).bg"
            :text-color="statusColor(item.status).text"
            class="font-weight-medium"
          >
            <v-icon
              x-small
              left
            >
              {{ statusIcon(item.status) }}
            </v-icon>
            {{ item.status }}
          </v-chip>
        </template>

        <!-- Last Used -->
        <template #item.lastUsed="{ item }">
          <span class="caption">{{ item.lastUsed || "—" }}</span>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn
                  icon
                  x-small
                  color="teal"
                  v-on="on"
                  @click="openAllocateDialog(item)"
                >
                  <v-icon x-small>
                    mdi-ev-station
                  </v-icon>
                </v-btn>
              </template>
              <span>Allocate to Charger</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn
                  icon
                  x-small
                  color="blue"
                  v-on="on"
                  @click="openEditDialog(item)"
                >
                  <v-icon x-small>
                    mdi-pencil
                  </v-icon>
                </v-btn>
              </template>
              <span>Edit Card</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn
                  icon
                  x-small
                  color="orange"
                  v-on="on"
                  @click="openHistoryDialog(item)"
                >
                  <v-icon x-small>
                    mdi-history
                  </v-icon>
                </v-btn>
              </template>
              <span>Transaction History</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on }">
                <v-btn
                  icon
                  x-small
                  :color="item.status === 'Active' ? 'red' : 'green'"
                  v-on="on"
                  @click="toggleStatus(item)"
                >
                  <v-icon x-small>
                    {{
                      item.status === "Active"
                        ? "mdi-cancel"
                        : "mdi-check-circle"
                    }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{
                item.status === "Active" ? "Revoke Card" : "Activate Card"
              }}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- DIALOG 1 — ADD RFID CARD                                       -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <v-dialog
      v-model="dialogs.add"
      max-width="560"
      persistent
    >
      <v-card>
        <v-card-title class="rfid-dialog__header">
          <v-icon
            color="teal"
            class="mr-2"
          >
            mdi-credit-card-plus
          </v-icon>
          <span>Add RFID Card</span>
          <v-spacer />
          <v-btn
            icon
            small
            @click="dialogs.add = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-4">
          <v-form
            ref="addForm"
            v-model="forms.addValid"
            lazy-validation
          >
            <!-- Step indicator -->
            <div class="rfid-steps mb-4">
              <div
                v-for="(step, i) in addSteps"
                :key="i"
                class="rfid-steps__item"
                :class="{
                  'rfid-steps__item--active': addStep >= i + 1,
                  'rfid-steps__item--done': addStep > i + 1,
                }"
              >
                <div class="rfid-steps__circle">
                  <v-icon
                    v-if="addStep > i + 1"
                    x-small
                    color="white"
                  >
                    mdi-check
                  </v-icon>
                  <span v-else>{{ i + 1 }}</span>
                </div>
                <span class="rfid-steps__label">{{ step }}</span>
                <div
                  v-if="i < addSteps.length - 1"
                  class="rfid-steps__line"
                />
              </div>
            </div>

            <!-- STEP 1 — Card Info -->
            <div v-if="addStep === 1">
              <div class="rfid-section-title mb-3">
                <v-icon
                  small
                  color="teal"
                  class="mr-1"
                >
                  mdi-card-account-details
                </v-icon>
                Card Details
              </div>

              <v-text-field
                v-model="addForm.cardUid"
                label="Card UID *"
                placeholder="e.g. 04:A3:2F:B1:9C:00:01"
                outlined
                dense
                color="teal"
                :rules="[rules.required, rules.uidFormat]"
                hint="ISO format — hex pairs separated by colons"
                persistent-hint
                class="mb-2"
              >
                <template #append>
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-icon
                        small
                        color="grey"
                        v-on="on"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>Scan the card or enter manually.<br>Supports ISO/IEC
                      14443-A/B, 18092, 15693</span>
                  </v-tooltip>
                </template>
              </v-text-field>

              <!-- <v-select
                v-model="addForm.cardType"
                :items="cardTypeOptions"
                label="Card Standard *"
                outlined
                dense
                color="teal"
                :rules="[rules.required]"
                class="mb-2"
              /> -->

              <v-text-field
                v-model="addForm.assignedTo"
                label="Assign to (Driver / User)"
                placeholder="e.g. Ahmed Khan"
                outlined
                dense
                color="teal"
                class="mb-2"
              />

              <v-text-field
                v-model="addForm.vehicleId"
                label="Vehicle ID"
                placeholder="e.g. VEH-KHI-001"
                outlined
                dense
                color="teal"
                class="mb-2"
              />

              <v-select
                v-model="addForm.tenantName"
                :items="tenantOptions"
                label="Tenant / Site *"
                outlined
                dense
                color="teal"
                :rules="[rules.required]"
                class="mb-2"
              />

              <v-menu
                v-model="expiryMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on }">
                  <v-text-field
                    v-model="addForm.expiryDate"
                    label="Expiry Date *"
                    readonly
                    outlined
                    dense
                    color="teal"
                    :rules="[rules.required]"
                    prepend-inner-icon="mdi-calendar"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="addForm.expiryDate"
                  :min="today"
                  color="teal"
                  @input="expiryMenu = false"
                />
              </v-menu>
            </div>

            <!-- STEP 2 — Allocate to Charger -->
            <div v-if="addStep === 2">
              <div class="rfid-section-title mb-3">
                <v-icon
                  small
                  color="teal"
                  class="mr-1"
                >
                  mdi-ev-station
                </v-icon>
                Allocate to Charger
              </div>

              <v-alert
                type="info"
                dense
                outlined
                color="teal"
                class="mb-3 caption"
              >
                Select the charger this RFID card will be linked to. The card
                will only authorize sessions on this charger.
              </v-alert>

              <v-select
                v-model="addForm.allocatedSite"
                :items="siteOptions"
                label="Site *"
                outlined
                dense
                color="teal"
                :rules="[rules.required]"
                class="mb-2"
                @change="addForm.allocatedCharger = null"
              />

              <v-select
                v-model="addForm.allocatedCharger"
                :items="chargersBySite(addForm.allocatedSite)"
                label="Charger ID *"
                outlined
                dense
                color="teal"
                :rules="[rules.required]"
                :disabled="!addForm.allocatedSite"
                no-data-text="Select a site first"
                class="mb-2"
              >
                <template #item="{ item }">
                  <div class="d-flex align-center py-1">
                    <v-icon
                      small
                      color="teal"
                      class="mr-2"
                    >
                      mdi-ev-station
                    </v-icon>
                    <div>
                      <div class="body-2 font-weight-medium">
                        {{ item.text }}
                      </div>
                      <div class="caption grey--text">
                        {{ item.location }}
                      </div>
                    </div>
                    <v-spacer />
                    <v-chip
                      x-small
                      :color="item.statusColor"
                      dark
                    >
                      {{ item.status }}
                    </v-chip>
                  </div>
                </template>
              </v-select>

              <!-- Charger preview card -->
              <v-card
                v-if="selectedChargerPreview"
                outlined
                class="rfid-charger-preview pa-3 mb-2"
              >
                <div class="d-flex align-center mb-2">
                  <v-icon
                    color="teal"
                    class="mr-2"
                  >
                    mdi-ev-station
                  </v-icon>
                  <span class="font-weight-bold">{{
                    selectedChargerPreview.id
                  }}</span>
                  <v-spacer />
                  <v-chip
                    x-small
                    :color="selectedChargerPreview.statusColor"
                    dark
                  >
                    {{ selectedChargerPreview.status }}
                  </v-chip>
                </div>
                <v-row dense>
                  <v-col cols="6">
                    <div class="caption grey--text">
                      Location
                    </div>
                    <div class="body-2">
                      {{ selectedChargerPreview.location }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="caption grey--text">
                      Power Type
                    </div>
                    <div class="body-2">
                      {{ selectedChargerPreview.powerType }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="caption grey--text">
                      Connectors
                    </div>
                    <div class="body-2">
                      {{ selectedChargerPreview.connectors }}
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="caption grey--text">
                      Protocol
                    </div>
                    <div class="body-2">
                      {{ selectedChargerPreview.protocol }}
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </div>

            <!-- STEP 3 — Authorization Mode -->
            <div v-if="addStep === 3">
              <div class="rfid-section-title mb-3">
                <v-icon
                  small
                  color="teal"
                  class="mr-1"
                >
                  mdi-shield-key
                </v-icon>
                Authorization Mode
              </div>

              <v-alert
                type="info"
                dense
                outlined
                color="teal"
                class="mb-3 caption"
              >
                Select the authorization mode applicable to charger
                <strong>{{ addForm.allocatedCharger }}</strong>. This determines how the CSMS validates this card during a
                session.
              </v-alert>

              <!-- Auth Mode Selection Cards -->
              <div
                v-for="mode in authModeDefinitions"
                :key="mode.value"
                class="rfid-auth-option mb-2"
                :class="{
                  'rfid-auth-option--selected': addForm.authMode === mode.value,
                }"
                @click="addForm.authMode = mode.value"
              >
                <div class="d-flex align-center">
                  <div class="rfid-auth-option__radio mr-3">
                    <v-icon
                      :color="
                        addForm.authMode === mode.value
                          ? 'teal'
                          : 'grey lighten-1'
                      "
                      small
                    >
                      {{
                        addForm.authMode === mode.value
                          ? "mdi-radiobox-marked"
                          : "mdi-radiobox-blank"
                      }}
                    </v-icon>
                  </div>
                  <v-icon
                    :color="mode.color"
                    class="mr-2"
                  >
                    {{ mode.icon }}
                  </v-icon>
                  <div class="flex-grow-1">
                    <div class="body-2 font-weight-bold">
                      {{ mode.label }}
                    </div>
                    <div class="caption grey--text">
                      {{ mode.description }}
                    </div>
                  </div>
                  <v-chip
                    x-small
                    :color="mode.badgeColor"
                    dark
                    class="ml-2"
                  >
                    {{ mode.badge }}
                  </v-chip>
                </div>
              </div>

              <v-alert
                v-if="addForm.authMode"
                dense
                outlined
                :type="selectedAuthModeInfo.alertType"
                class="mt-3 caption"
              >
                <strong>{{ selectedAuthModeInfo.label }}:</strong>
                {{ selectedAuthModeInfo.detail }}
              </v-alert>
            </div>

            <!-- STEP 4 — Review & Confirm -->
            <div v-if="addStep === 4">
              <div class="rfid-section-title mb-3">
                <v-icon
                  small
                  color="teal"
                  class="mr-1"
                >
                  mdi-check-decagram
                </v-icon>
                Review & Confirm
              </div>

              <v-card
                outlined
                class="pa-3 mb-3"
              >
                <div
                  class="caption grey--text mb-2 text-uppercase font-weight-bold"
                >
                  Card Details
                </div>
                <v-row dense>
                  <v-col
                    cols="5"
                    class="caption grey--text"
                  >
                    Card UID
                  </v-col>
                  <v-col
                    cols="7"
                    class="caption font-weight-medium teal--text"
                  >
                    {{ addForm.cardUid }}
                  </v-col>
                  <v-col
                    cols="5"
                    class="caption grey--text"
                  >
                    Standard
                  </v-col>
                  <v-col
                    cols="7"
                    class="caption"
                  >
                    {{ addForm.cardType }}
                  </v-col>
                  <v-col
                    cols="5"
                    class="caption grey--text"
                  >
                    Assigned To
                  </v-col>
                  <v-col
                    cols="7"
                    class="caption"
                  >
                    {{ addForm.assignedTo || "—" }}
                  </v-col>
                  <v-col
                    cols="5"
                    class="caption grey--text"
                  >
                    Vehicle ID
                  </v-col>
                  <v-col
                    cols="7"
                    class="caption"
                  >
                    {{ addForm.vehicleId || "—" }}
                  </v-col>
                  <v-col
                    cols="5"
                    class="caption grey--text"
                  >
                    Tenant
                  </v-col>
                  <v-col
                    cols="7"
                    class="caption"
                  >
                    {{ addForm.tenantName }}
                  </v-col>
                  <v-col
                    cols="5"
                    class="caption grey--text"
                  >
                    Expiry
                  </v-col>
                  <v-col
                    cols="7"
                    class="caption"
                  >
                    {{ addForm.expiryDate }}
                  </v-col>
                </v-row>
              </v-card>

              <v-card
                outlined
                class="pa-3 mb-3"
              >
                <div
                  class="caption grey--text mb-2 text-uppercase font-weight-bold"
                >
                  Allocation
                </div>
                <v-row dense>
                  <v-col
                    cols="5"
                    class="caption grey--text"
                  >
                    Site
                  </v-col>
                  <v-col
                    cols="7"
                    class="caption font-weight-medium"
                  >
                    {{ addForm.allocatedSite }}
                  </v-col>
                  <v-col
                    cols="5"
                    class="caption grey--text"
                  >
                    Charger ID
                  </v-col>
                  <v-col
                    cols="7"
                    class="caption font-weight-medium teal--text"
                  >
                    {{ addForm.allocatedCharger }}
                  </v-col>
                </v-row>
              </v-card>

              <v-card
                outlined
                class="pa-3"
              >
                <div
                  class="caption grey--text mb-2 text-uppercase font-weight-bold"
                >
                  Authorization Mode
                </div>
                <div class="d-flex align-center">
                  <v-icon
                    :color="selectedAuthModeInfo.color"
                    class="mr-2"
                  >
                    {{ selectedAuthModeInfo.icon }}
                  </v-icon>
                  <div>
                    <div class="body-2 font-weight-bold">
                      {{ addForm.authMode }}
                    </div>
                    <div class="caption grey--text">
                      {{ selectedAuthModeInfo.description }}
                    </div>
                  </div>
                </div>
              </v-card>
            </div>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-btn
            text
            @click="dialogs.add = false"
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="addStep > 1"
            outlined
            color="grey"
            @click="addStep--"
          >
            <v-icon
              left
              small
            >
              mdi-arrow-left
            </v-icon>
            Back
          </v-btn>
          <v-btn
            v-if="addStep < 4"
            color="teal"
            dark
            @click="nextStep"
          >
            Next
            <v-icon
              right
              small
            >
              mdi-arrow-right
            </v-icon>
          </v-btn>
          <v-btn
            v-if="addStep === 4"
            color="teal"
            dark
            :loading="saving"
            @click="saveCard"
          >
            <v-icon
              left
              small
            >
              mdi-check
            </v-icon>
            Save & Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- DIALOG 2 — ALLOCATE / EDIT ALLOCATION                         -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <v-dialog
      v-model="dialogs.allocate"
      max-width="520"
      persistent
    >
      <v-card v-if="selectedCard">
        <v-card-title class="rfid-dialog__header">
          <v-icon
            color="teal"
            class="mr-2"
          >
            mdi-ev-station
          </v-icon>
          <span>Allocate to Charger</span>
          <v-spacer />
          <v-btn
            icon
            small
            @click="dialogs.allocate = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-4">
          <!-- Card badge -->
          <v-card
            outlined
            color="teal lighten-5"
            class="pa-3 mb-4"
          >
            <div class="d-flex align-center">
              <v-icon
                color="teal"
                class="mr-2"
              >
                mdi-credit-card-wireless
              </v-icon>
              <div>
                <div class="body-2 font-weight-bold teal--text">
                  {{ selectedCard.cardUid }}
                </div>
                <div class="caption grey--text">
                  {{ selectedCard.cardType }} ·
                  {{ selectedCard.assignedTo || "Unassigned" }}
                </div>
              </div>
              <v-spacer />
              <v-chip
                x-small
                :color="statusColor(selectedCard.status).bg"
                :text-color="statusColor(selectedCard.status).text"
              >
                {{ selectedCard.status }}
              </v-chip>
            </div>
          </v-card>

          <v-form
            ref="allocateForm"
            v-model="forms.allocateValid"
          >
            <div class="rfid-section-title mb-2">
              <v-icon
                small
                color="teal"
                class="mr-1"
              >
                mdi-map-marker
              </v-icon>
              Select Charger
            </div>

            <v-select
              v-model="allocateForm.site"
              :items="siteOptions"
              label="Site *"
              outlined
              dense
              color="teal"
              :rules="[rules.required]"
              class="mb-2"
              @change="allocateForm.chargerId = null"
            />

            <v-select
              v-model="allocateForm.chargerId"
              :items="chargersBySite(allocateForm.site)"
              label="Charger ID *"
              outlined
              dense
              color="teal"
              :rules="[rules.required]"
              :disabled="!allocateForm.site"
              class="mb-3"
            >
              <template #item="{ item }">
                <div class="d-flex align-center py-1 full-width">
                  <v-icon
                    small
                    color="teal"
                    class="mr-2"
                  >
                    mdi-ev-station
                  </v-icon>
                  <div>
                    <div class="body-2 font-weight-medium">
                      {{ item.text }}
                    </div>
                    <div class="caption grey--text">
                      {{ item.location }}
                    </div>
                  </div>
                  <v-spacer />
                  <v-chip
                    x-small
                    :color="item.statusColor"
                    dark
                  >
                    {{ item.status }}
                  </v-chip>
                </div>
              </template>
            </v-select>

            <v-divider class="mb-3" />

            <div class="rfid-section-title mb-2">
              <v-icon
                small
                color="teal"
                class="mr-1"
              >
                mdi-shield-key
              </v-icon>
              Authorization Mode
            </div>

            <div
              v-for="mode in authModeDefinitions"
              :key="mode.value"
              class="rfid-auth-option mb-2"
              :class="{
                'rfid-auth-option--selected':
                  allocateForm.authMode === mode.value,
              }"
              @click="allocateForm.authMode = mode.value"
            >
              <div class="d-flex align-center">
                <v-icon
                  :color="
                    allocateForm.authMode === mode.value
                      ? 'teal'
                      : 'grey lighten-1'
                  "
                  small
                  class="mr-2"
                >
                  {{
                    allocateForm.authMode === mode.value
                      ? "mdi-radiobox-marked"
                      : "mdi-radiobox-blank"
                  }}
                </v-icon>
                <v-icon
                  :color="mode.color"
                  small
                  class="mr-2"
                >
                  {{ mode.icon }}
                </v-icon>
                <div>
                  <div class="body-2 font-weight-medium">
                    {{ mode.label }}
                  </div>
                  <div class="caption grey--text">
                    {{ mode.description }}
                  </div>
                </div>
                <v-spacer />
                <v-chip
                  x-small
                  :color="mode.badgeColor"
                  dark
                >
                  {{ mode.badge }}
                </v-chip>
              </div>
            </div>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-btn
            v-if="selectedCard.allocatedCharger"
            text
            color="red"
            @click="deallocateCard"
          >
            <v-icon
              left
              small
            >
              mdi-link-off
            </v-icon>
            Remove Allocation
          </v-btn>
          <v-spacer />
          <v-btn
            text
            @click="dialogs.allocate = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="teal"
            dark
            :loading="saving"
            :disabled="!allocateForm.chargerId || !allocateForm.authMode"
            @click="saveAllocation"
          >
            <v-icon
              left
              small
            >
              mdi-check
            </v-icon>
            Apply Settings
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- DIALOG 3 — TRANSACTION HISTORY                                 -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <v-dialog
      v-model="dialogs.history"
      max-width="680"
    >
      <v-card v-if="selectedCard">
        <v-card-title class="rfid-dialog__header">
          <v-icon
            color="orange"
            class="mr-2"
          >
            mdi-history
          </v-icon>
          <span>Transaction History</span>
          <v-spacer />
          <v-btn
            icon
            small
            @click="dialogs.history = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-3">
          <!-- Card summary -->
          <v-card
            outlined
            color="teal lighten-5"
            class="pa-2 mb-3"
          >
            <div class="d-flex align-center">
              <v-icon
                color="teal"
                small
                class="mr-2"
              >
                mdi-credit-card-wireless
              </v-icon>
              <span class="body-2 font-weight-bold teal--text">{{
                selectedCard.cardUid
              }}</span>
              <span class="caption grey--text mx-2">·</span>
              <span class="caption">{{
                selectedCard.assignedTo || "Unassigned"
              }}</span>
              <v-spacer />
              <span
                class="caption grey--text"
              >{{ transactionHistory.length }} sessions total</span>
            </div>
          </v-card>

          <v-data-table
            :headers="historyHeaders"
            :items="transactionHistory"
            :items-per-page="5"
            dense
            class="rfid-history-table"
          >
            <template #item.result="{ item }">
              <v-chip
                x-small
                :color="item.result === 'Accepted' ? 'green' : 'red'"
                dark
              >
                {{ item.result }}
              </v-chip>
            </template>
            <template #item.energy="{ item }">
              <span class="caption">{{ item.energy }} kWh</span>
            </template>
            <template #item.cost="{ item }">
              <span
                class="caption font-weight-medium"
              >PKR {{ item.cost }}</span>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-3">
          <v-btn
            outlined
            color="teal"
            small
          >
            <v-icon
              left
              small
            >
              mdi-download
            </v-icon>
            Export CSV
          </v-btn>
          <v-spacer />
          <v-btn
            text
            @click="dialogs.history = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ═══════════════════════════════════════════════════════════════ -->
    <!-- DIALOG 4 — CSV IMPORT                                          -->
    <!-- ═══════════════════════════════════════════════════════════════ -->
    <v-dialog
      v-model="dialogs.import"
      max-width="520"
    >
      <v-card>
        <v-card-title class="rfid-dialog__header">
          <v-icon
            color="teal"
            class="mr-2"
          >
            mdi-upload
          </v-icon>
          <span>Bulk CSV Import</span>
          <v-spacer />
          <v-btn
            icon
            small
            @click="dialogs.import = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-4">
          <v-alert
            dense
            outlined
            color="teal"
            type="info"
            class="caption mb-3"
          >
            Upload a CSV file to import multiple RFID cards at once. ISO UID
            format will be validated on import.
          </v-alert>

          <div
            class="rfid-upload-zone mb-3"
            @click="$refs.csvInput.click()"
          >
            <v-icon
              size="40"
              color="grey lighten-1"
            >
              mdi-file-upload
            </v-icon>
            <div class="body-2 grey--text mt-2">
              Click to upload or drag & drop
            </div>
            <div class="caption grey--text">
              CSV files only · Max 5MB
            </div>
            <input
              ref="csvInput"
              type="file"
              accept=".csv"
              hidden
              @change="handleCsvUpload"
            >
          </div>

          <v-card
            outlined
            class="pa-2 mb-3"
          >
            <div class="caption grey--text font-weight-bold mb-1">
              Required CSV Columns:
            </div>
            <div class="caption font-family-monospace">
              card_uid, card_type, assigned_to, vehicle_id, tenant_name,
              expiry_date
            </div>
          </v-card>

          <v-btn
            text
            color="teal"
            small
          >
            <v-icon
              left
              small
            >
              mdi-download
            </v-icon>
            Download Template CSV
          </v-btn>
        </v-card-text>

        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn
            text
            @click="dialogs.import = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="teal"
            dark
            disabled
          >
            <v-icon
              left
              small
            >
              mdi-upload
            </v-icon>
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── SNACKBAR ──────────────────────────────────────────────────── -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      bottom
      right
    >
      <v-icon
        small
        class="mr-2"
      >
        {{ snackbar.icon }}
      </v-icon>
      {{ snackbar.message }}
      <template #action>
        <v-btn
          text
          small
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
// ─── DUMMY DATA ────────────────────────────────────────────────────────────────
const RFID_DATA = [
  {
    id: 1,
    cardUid: "04:A3:2F:B1:9C:00:01",
    cardType: "ISO/IEC 14443-A",
    assignedTo: "Ahmed Khan",
    vehicleId: "VEH-KHI-001",
    tenantName: "PSO Karachi Hub",
    status: "Active",
    authMode: "Online with RFID Auth",
    allocatedCharger: "CP-ORK-KHI-001",
    allocatedSite: "PSO Karachi Hub",
    lastUsed: "2025-05-08 14:42",
    expiryDate: "2026-12-31",
    createdAt: "2024-01-10",
  },
  {
    id: 2,
    cardUid: "04:B7:1A:C4:2D:00:02",
    cardType: "ISO/IEC 14443-A",
    assignedTo: "Bilal Ahmed",
    vehicleId: "VEH-KHI-002",
    tenantName: "PSO Karachi Hub",
    status: "Active",
    authMode: "Online with RFID Auth",
    allocatedCharger: "CP-ORK-KHI-002",
    allocatedSite: "PSO Karachi Hub",
    lastUsed: "2025-05-08 13:20",
    expiryDate: "2026-06-30",
    createdAt: "2024-02-14",
  },
  {
    id: 3,
    cardUid: "04:C9:3B:D5:4E:00:03",
    cardType: "ISO/IEC 14443-B",
    assignedTo: "Sara Malik",
    vehicleId: "VEH-KHI-003",
    tenantName: "PSO Karachi Hub",
    status: "Revoked",
    authMode: null,
    allocatedCharger: null,
    allocatedSite: null,
    lastUsed: "2025-04-22 11:05",
    expiryDate: "2025-12-31",
    createdAt: "2024-03-01",
  },
  {
    id: 4,
    cardUid: "04:D1:4C:E6:5F:00:04",
    cardType: "IEC/ISO 15693",
    assignedTo: "Usman Ali",
    vehicleId: "VEH-LHR-001",
    tenantName: "PSO Lahore Central",
    status: "Active",
    authMode: "Local Auth List",
    allocatedCharger: "CP-ORK-LHR-001",
    allocatedSite: "PSO Lahore Central",
    lastUsed: "2025-05-08 14:10",
    expiryDate: "2027-03-31",
    createdAt: "2024-01-20",
  },
  {
    id: 5,
    cardUid: "04:E2:5D:F7:6A:00:05",
    cardType: "ISO/IEC 14443-A",
    assignedTo: "Fatima Zahra",
    vehicleId: "VEH-LHR-002",
    tenantName: "PSO Lahore Central",
    status: "Active",
    authMode: "Offline with Local List",
    allocatedCharger: "CP-ORK-LHR-002",
    allocatedSite: "PSO Lahore Central",
    lastUsed: "2025-05-07 18:35",
    expiryDate: "2026-09-30",
    createdAt: "2024-04-05",
  },
  {
    id: 6,
    cardUid: "04:F3:6E:A8:7B:00:06",
    cardType: "ISO/IEC 18092",
    assignedTo: "Hassan Raza",
    vehicleId: "VEH-LHR-003",
    tenantName: "PSO Lahore Central",
    status: "Blocked",
    authMode: "Online with RFID Auth",
    allocatedCharger: "CP-ORK-LHR-003",
    allocatedSite: "PSO Lahore Central",
    lastUsed: "2025-05-01 09:14",
    expiryDate: "2026-12-31",
    createdAt: "2024-05-10",
  },
  {
    id: 7,
    cardUid: "04:A4:7F:B9:8C:00:07",
    cardType: "ISO/IEC 14443-A",
    assignedTo: "Zubair Sheikh",
    vehicleId: "VEH-ISB-001",
    tenantName: "PSO Islamabad North",
    status: "Active",
    authMode: "Remote Auth (OCPP)",
    allocatedCharger: "CP-ORK-ISB-001",
    allocatedSite: "PSO Islamabad North",
    lastUsed: "2025-05-08 12:58",
    expiryDate: "2027-06-30",
    createdAt: "2024-01-15",
  },
  {
    id: 8,
    cardUid: "04:B5:8A:C0:9D:00:08",
    cardType: "ISO/IEC 14443-B",
    assignedTo: "Amina Hussain",
    vehicleId: "VEH-ISB-002",
    tenantName: "PSO Islamabad North",
    status: "Active",
    authMode: "Local Auth List",
    allocatedCharger: "CP-ORK-ISB-002",
    allocatedSite: "PSO Islamabad North",
    lastUsed: "2025-05-08 11:42",
    expiryDate: "2026-03-31",
    createdAt: "2024-06-20",
  },
  {
    id: 9,
    cardUid: "04:C6:9B:D1:0E:00:09",
    cardType: "IEC/ISO 15693",
    assignedTo: "Tariq Mahmood",
    vehicleId: "VEH-ISB-003",
    tenantName: "PSO Islamabad North",
    status: "Expired",
    authMode: null,
    allocatedCharger: null,
    allocatedSite: null,
    lastUsed: "2025-03-15 16:22",
    expiryDate: "2025-03-31",
    createdAt: "2023-04-01",
  },
  {
    id: 10,
    cardUid: "04:D7:0C:E2:1F:00:10",
    cardType: "ISO/IEC 14443-A",
    assignedTo: "Ali Hassan",
    vehicleId: "VEH-PSH-001",
    tenantName: "PSO Peshawar Gate",
    status: "Active",
    authMode: "Online with RFID Auth",
    allocatedCharger: "CP-ORK-PSH-001",
    allocatedSite: "PSO Peshawar Gate",
    lastUsed: "2025-05-08 10:30",
    expiryDate: "2026-12-31",
    createdAt: "2024-07-01",
  },
  {
    id: 11,
    cardUid: "04:E8:1D:F3:2A:00:11",
    cardType: "ISO/IEC 14443-A",
    assignedTo: "Sana Iqbal",
    vehicleId: "VEH-PSH-002",
    tenantName: "PSO Peshawar Gate",
    status: "Active",
    authMode: "Offline with Local List",
    allocatedCharger: null,
    allocatedSite: null,
    lastUsed: "2025-05-07 15:48",
    expiryDate: "2027-01-31",
    createdAt: "2024-08-12",
  },
  {
    id: 12,
    cardUid: "04:F9:2E:A4:3B:00:12",
    cardType: "ISO/IEC 18092",
    assignedTo: "Kamran Baig",
    vehicleId: "VEH-PSH-003",
    tenantName: "PSO Peshawar Gate",
    status: "Revoked",
    authMode: null,
    allocatedCharger: null,
    allocatedSite: null,
    lastUsed: "2025-04-10 08:55",
    expiryDate: "2026-06-30",
    createdAt: "2024-02-28",
  },
  {
    id: 13,
    cardUid: "04:A0:3F:B5:4C:00:13",
    cardType: "ISO/IEC 14443-A",
    assignedTo: "Nadia Pervez",
    vehicleId: "VEH-MUL-001",
    tenantName: "PSO Multan Depot",
    status: "Active",
    authMode: "Remote Auth (OCPP)",
    allocatedCharger: "CP-ORK-MUL-001",
    allocatedSite: "PSO Multan Depot",
    lastUsed: "2025-05-08 09:18",
    expiryDate: "2026-12-31",
    createdAt: "2024-03-15",
  },
  {
    id: 14,
    cardUid: "04:B1:4A:C6:5D:00:14",
    cardType: "ISO/IEC 14443-B",
    assignedTo: "Imran Qureshi",
    vehicleId: "VEH-MUL-002",
    tenantName: "PSO Multan Depot",
    status: "Active",
    authMode: "Local Auth List",
    allocatedCharger: "CP-ORK-MUL-002",
    allocatedSite: "PSO Multan Depot",
    lastUsed: "2025-05-06 14:05",
    expiryDate: "2027-09-30",
    createdAt: "2024-09-01",
  },
  {
    id: 15,
    cardUid: "04:C2:5B:D7:6E:00:15",
    cardType: "IEC/ISO 15693",
    assignedTo: "Rabia Noor",
    vehicleId: "VEH-RWP-001",
    tenantName: "PSO Rawalpindi Central",
    status: "Active",
    authMode: "Online with RFID Auth",
    allocatedCharger: "CP-ORK-RWP-001",
    allocatedSite: "PSO Rawalpindi Central",
    lastUsed: "2025-05-08 13:50",
    expiryDate: "2026-12-31",
    createdAt: "2024-10-10",
  },
];

// ─── CHARGER MASTER DATA ──────────────────────────────────────────────────────
const CHARGERS = [
  {
    id: "CP-ORK-KHI-001",
    site: "PSO Karachi Hub",
    location: "Block 7, Clifton",
    powerType: "AC 22kW",
    connectors: 2,
    protocol: "OCPP 1.6J",
    status: "Available",
    statusColor: "green",
  },
  {
    id: "CP-ORK-KHI-002",
    site: "PSO Karachi Hub",
    location: "Block 7, Clifton",
    powerType: "DC 50kW",
    connectors: 2,
    protocol: "OCPP 1.6J",
    status: "Charging",
    statusColor: "teal",
  },
  {
    id: "CP-ORK-KHI-003",
    site: "PSO Karachi Hub",
    location: "Block 7, Clifton",
    powerType: "AC 22kW",
    connectors: 1,
    protocol: "OCPP 2.0.1",
    status: "Faulted",
    statusColor: "red",
  },
  {
    id: "CP-ORK-LHR-001",
    site: "PSO Lahore Central",
    location: "Gulberg III",
    powerType: "DC 50kW",
    connectors: 4,
    protocol: "OCPP 1.6J",
    status: "Available",
    statusColor: "green",
  },
  {
    id: "CP-ORK-LHR-002",
    site: "PSO Lahore Central",
    location: "Gulberg III",
    powerType: "AC 22kW",
    connectors: 2,
    protocol: "OCPP 1.6J",
    status: "Available",
    statusColor: "green",
  },
  {
    id: "CP-ORK-LHR-003",
    site: "PSO Lahore Central",
    location: "DHA Phase 5",
    powerType: "DC 50kW",
    connectors: 2,
    protocol: "OCPP 2.0.1",
    status: "Offline",
    statusColor: "grey",
  },
  {
    id: "CP-ORK-ISB-001",
    site: "PSO Islamabad North",
    location: "F-10 Markaz",
    powerType: "AC 22kW",
    connectors: 2,
    protocol: "OCPP 1.6J",
    status: "Available",
    statusColor: "green",
  },
  {
    id: "CP-ORK-ISB-002",
    site: "PSO Islamabad North",
    location: "F-10 Markaz",
    powerType: "DC 50kW",
    connectors: 2,
    protocol: "OCPP 1.6J",
    status: "Charging",
    statusColor: "teal",
  },
  {
    id: "CP-ORK-PSH-001",
    site: "PSO Peshawar Gate",
    location: "University Road",
    powerType: "AC 22kW",
    connectors: 2,
    protocol: "OCPP 1.6J",
    status: "Available",
    statusColor: "green",
  },
  {
    id: "CP-ORK-MUL-001",
    site: "PSO Multan Depot",
    location: "Bosan Road",
    powerType: "AC 22kW",
    connectors: 2,
    protocol: "OCPP 1.6J",
    status: "Available",
    statusColor: "green",
  },
  {
    id: "CP-ORK-MUL-002",
    site: "PSO Multan Depot",
    location: "Bosan Road",
    powerType: "DC 50kW",
    connectors: 1,
    protocol: "OCPP 2.0.1",
    status: "Available",
    statusColor: "green",
  },
  {
    id: "CP-ORK-RWP-001",
    site: "PSO Rawalpindi Central",
    location: "Saddar",
    powerType: "AC 22kW",
    connectors: 2,
    protocol: "OCPP 1.6J",
    status: "Available",
    statusColor: "green",
  },
];

// ─── AUTH MODE DEFINITIONS (from WBS 1.3.2 — local auth list + remote auth) ───
const AUTH_MODE_DEFINITIONS = [
  {
    value: "Online with RFID Auth",
    label: "Online with RFID Auth",
    description:
      "CSMS validates the RFID tag in real-time via OCPP Authorize.req before allowing a session to start.",
    detail:
      "Requires active network connection. Most secure — each swipe is validated live against the CSMS whitelist.",
    icon: "mdi-wifi",
    color: "teal",
    badge: "Recommended",
    badgeColor: "teal",
    alertType: "info",
  },
  {
    value: "Local Auth List",
    label: "Local Auth List",
    description:
      "Charger uses a locally stored whitelist of approved RFID tags for authorization. No real-time CSMS call.",
    detail:
      "Works offline. CSMS periodically syncs the local list. Session starts immediately without network round-trip.",
    icon: "mdi-database",
    color: "blue",
    badge: "Offline Safe",
    badgeColor: "blue",
    alertType: "info",
  },
  {
    value: "Offline with Local List",
    label: "Offline with Local List",
    description:
      "Falls back to local list when CSMS is unreachable. Preferred for sites with intermittent connectivity.",
    detail:
      "Hybrid mode — tries online first, falls back to local list if CSMS is unreachable within timeout.",
    icon: "mdi-wifi-off",
    color: "orange",
    badge: "Hybrid",
    badgeColor: "orange",
    alertType: "warning",
  },
  {
    value: "Remote Auth (OCPP)",
    label: "Remote Auth (OCPP)",
    description:
      "Authorization handled entirely by the CSMS over OCPP. Charger waits for Authorize.conf response.",
    detail:
      "OCPP 1.6J / 2.0.1 Authorize message flow. Full audit trail per authorization. Requires stable connectivity.",
    icon: "mdi-cloud-check",
    color: "purple",
    badge: "OCPP",
    badgeColor: "purple",
    alertType: "info",
  },
];

export default {
  name: "RfidManagement",

  data() {
    return {
      loading: false,
      saving: false,
      expiryMenu: false,
      today: new Date().toISOString().substr(0, 10),

      // ── Table data ────────────────────────────────────────────────
      cards: JSON.parse(JSON.stringify(RFID_DATA)),
      authModeDefinitions: AUTH_MODE_DEFINITIONS,

      // ── Dialog visibility ─────────────────────────────────────────
      dialogs: { add: false, allocate: false, history: false, import: false },

      // ── Selected card (for allocate / history dialogs) ────────────
      selectedCard: null,

      // ── Add form — 4-step wizard ──────────────────────────────────
      addStep: 1,
      addSteps: ["Card Info", "Allocate Charger", "Auth Mode", "Review"],
      addForm: this.emptyAddForm(),
      forms: { addValid: false, allocateValid: false },

      // ── Allocate form ─────────────────────────────────────────────
      allocateForm: { site: null, chargerId: null, authMode: null },

      // ── Filters ───────────────────────────────────────────────────
      filters: {
        search: "",
        status: null,
        authMode: null,
        tenant: null,
        allocated: null,
      },

      // ── Snackbar ──────────────────────────────────────────────────
      snackbar: { show: false, message: "", color: "teal", icon: "mdi-check" },

      // ── Table headers ─────────────────────────────────────────────
      tableHeaders: [
        { text: "Card UID", value: "cardUid", sortable: true, width: "200" },
        { text: "Standard", value: "cardType", sortable: true, width: "140" },
        {
          text: "Assigned To",
          value: "assignedTo",
          sortable: true,
          width: "150",
        },
        {
          text: "Allocated Charger",
          value: "allocatedCharger",
          sortable: true,
          width: "180",
        },
        { text: "Auth Mode", value: "authMode", sortable: true, width: "200" },
        { text: "Status", value: "status", sortable: true, width: "110" },
        { text: "Last Used", value: "lastUsed", sortable: true, width: "140" },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "120",
          align: "center",
        },
      ],

      historyHeaders: [
        { text: "Session ID", value: "sessionId", sortable: false },
        { text: "Charger", value: "chargerId", sortable: false },
        { text: "Start", value: "start", sortable: false },
        { text: "Duration", value: "duration", sortable: false },
        { text: "Energy", value: "energy", sortable: false },
        { text: "Cost", value: "cost", sortable: false },
        { text: "Result", value: "result", sortable: false },
      ],

      // ── Validation rules ──────────────────────────────────────────
      rules: {
        required: (v) => !!v || "This field is required",
        uidFormat: (v) => {
          if (!v) return true;
          const hexPair = "[0-9A-Fa-f]{2}";
          const pattern = new RegExp(`^(${hexPair}:){3,6}${hexPair}$`);
          return (
            pattern.test(v) ||
            "Invalid UID format — use hex pairs separated by colons (e.g. 04:A3:2F:B1)"
          );
        },
      },
    };
  },

  computed: {
    // ── KPI cards ─────────────────────────────────────────────────────
    kpiCards() {
      const c = this.cards;
      return [
        {
          label: "Total Cards",
          value: c.length,
          icon: "mdi-credit-card-wireless",
          color: "#00bfa5",
        },
        {
          label: "Active",
          value: c.filter((x) => x.status === "Active").length,
          icon: "mdi-check-circle",
          color: "#22c55e",
        },
        {
          label: "Allocated",
          value: c.filter((x) => x.allocatedCharger).length,
          icon: "mdi-ev-station",
          color: "#00bfa5",
        },
        {
          label: "Unallocated",
          value: c.filter((x) => !x.allocatedCharger && x.status === "Active")
            .length,
          icon: "mdi-link-off",
          color: "#f59e0b",
        },
        {
          label: "Revoked",
          value: c.filter((x) => x.status === "Revoked").length,
          icon: "mdi-cancel",
          color: "#ef4444",
        },
        {
          label: "Expired",
          value: c.filter((x) => x.status === "Expired").length,
          icon: "mdi-clock-alert",
          color: "#9ca3af",
        },
      ];
    },

    // ── Filter options ────────────────────────────────────────────────
    statusOptions() {
      return ["Active", "Revoked", "Blocked", "Expired"];
    },
    authModeOptions() {
      return AUTH_MODE_DEFINITIONS.map((m) => m.value);
    },
    tenantOptions() {
      return [...new Set(this.cards.map((c) => c.tenantName))];
    },
    siteOptions() {
      return [...new Set(CHARGERS.map((c) => c.site))];
    },

    // ── Filtered table data ───────────────────────────────────────────
    filteredCards() {
      return this.cards.filter((card) => {
        const s = this.filters.search?.toLowerCase() || "";
        if (
          s &&
          !(
            card.cardUid.toLowerCase().includes(s) ||
            (card.assignedTo || "").toLowerCase().includes(s) ||
            (card.allocatedCharger || "").toLowerCase().includes(s) ||
            (card.vehicleId || "").toLowerCase().includes(s)
          )
        )
          return false;
        if (this.filters.status && card.status !== this.filters.status)
          return false;
        if (this.filters.authMode && card.authMode !== this.filters.authMode)
          return false;
        if (this.filters.tenant && card.tenantName !== this.filters.tenant)
          return false;
        if (this.filters.allocated !== null) {
          if (this.filters.allocated && !card.allocatedCharger) return false;
          if (!this.filters.allocated && card.allocatedCharger) return false;
        }
        return true;
      });
    },

    // ── Selected charger preview (in add wizard step 2) ───────────────
    selectedChargerPreview() {
      if (!this.addForm.allocatedCharger) return null;
      return (
        CHARGERS.find((c) => c.id === this.addForm.allocatedCharger) || null
      );
    },

    // ── Auth mode info for currently selected mode ────────────────────
    selectedAuthModeInfo() {
      const mode = AUTH_MODE_DEFINITIONS.find(
        (m) => m.value === this.addForm.authMode
      );
      return (
        mode || {
          label: "",
          description: "",
          detail: "",
          icon: "",
          color: "grey",
          alertType: "info",
        }
      );
    },

    // ── Transaction history (mock data per card) ──────────────────────
    transactionHistory() {
      if (!this.selectedCard) return [];
      return [
        {
          sessionId: "SES-0517",
          chargerId: this.selectedCard.allocatedCharger || "CP-ORK-KHI-001",
          start: "2025-05-08 14:32",
          duration: "42 min",
          energy: 14.8,
          cost: 850,
          result: "Accepted",
        },
        {
          sessionId: "SES-0488",
          chargerId: this.selectedCard.allocatedCharger || "CP-ORK-KHI-001",
          start: "2025-05-07 18:10",
          duration: "38 min",
          energy: 12.4,
          cost: 710,
          result: "Accepted",
        },
        {
          sessionId: "SES-0421",
          chargerId: this.selectedCard.allocatedCharger || "CP-ORK-KHI-001",
          start: "2025-05-06 09:55",
          duration: "55 min",
          energy: 18.2,
          cost: 1040,
          result: "Accepted",
        },
        {
          sessionId: "SES-0390",
          chargerId: this.selectedCard.allocatedCharger || "CP-ORK-KHI-001",
          start: "2025-05-04 17:22",
          duration: "12 min",
          energy: 3.2,
          cost: 180,
          result: "Accepted",
        },
        {
          sessionId: "SES-0341",
          chargerId: this.selectedCard.allocatedCharger || "CP-ORK-KHI-001",
          start: "2025-05-02 11:05",
          duration: "0 min",
          energy: 0,
          cost: 0,
          result: "Rejected",
        },
      ];
    },
  },

  methods: {
    // ── Helpers ───────────────────────────────────────────────────────
    emptyAddForm() {
      return {
        cardUid: "",
        cardType: null,
        assignedTo: "",
        vehicleId: "",
        tenantName: null,
        expiryDate: null,
        allocatedSite: null,
        allocatedCharger: null,
        authMode: null,
      };
    },

    chargersBySite(site) {
      if (!site) return [];
      return CHARGERS.filter((c) => c.site === site).map((c) => ({
        text: c.id,
        value: c.id,
        location: c.location,
        status: c.status,
        statusColor: c.statusColor,
        powerType: c.powerType,
        connectors: c.connectors,
        protocol: c.protocol,
      }));
    },

    // ── Color helpers ─────────────────────────────────────────────────
    statusColor(status) {
      const map = {
        Active: { bg: "green lighten-4", text: "green darken-3" },
        Revoked: { bg: "red lighten-4", text: "red darken-3" },
        Blocked: { bg: "amber lighten-4", text: "amber darken-3" },
        Expired: { bg: "grey lighten-3", text: "grey darken-2" },
      };
      return map[status] || { bg: "grey lighten-3", text: "grey darken-2" };
    },
    statusIcon(status) {
      return (
        {
          Active: "mdi-check-circle",
          Revoked: "mdi-cancel",
          Blocked: "mdi-block-helper",
          Expired: "mdi-clock-alert",
        }[status] || "mdi-help"
      );
    },
    cardTypeColor(type) {
      const map = {
        "ISO/IEC 14443-A": { bg: "teal lighten-4", text: "teal darken-3" },
        "ISO/IEC 14443-B": { bg: "cyan lighten-4", text: "cyan darken-3" },
        "ISO/IEC 18092": { bg: "indigo lighten-4", text: "indigo darken-3" },
        "IEC/ISO 15693": { bg: "blue lighten-4", text: "blue darken-3" },
      };
      return map[type] || { bg: "grey lighten-3", text: "grey darken-2" };
    },
    authModeColor(mode) {
      const map = {
        "Online with RFID Auth": {
          bg: "teal lighten-4",
          text: "teal darken-3",
        },
        "Local Auth List": { bg: "blue lighten-4", text: "blue darken-3" },
        "Offline with Local List": {
          bg: "orange lighten-4",
          text: "orange darken-3",
        },
        "Remote Auth (OCPP)": {
          bg: "purple lighten-4",
          text: "purple darken-3",
        },
      };
      return map[mode] || { bg: "grey lighten-3", text: "grey darken-2" };
    },
    authModeIcon(mode) {
      return (
        {
          "Online with RFID Auth": "mdi-wifi",
          "Local Auth List": "mdi-database",
          "Offline with Local List": "mdi-wifi-off",
          "Remote Auth (OCPP)": "mdi-cloud-check",
        }[mode] || "mdi-shield-key"
      );
    },

    // ── Filter ────────────────────────────────────────────────────────
    clearFilters() {
      this.filters = {
        search: "",
        status: null,
        authMode: null,
        tenant: null,
        allocated: null,
      };
    },

    // ── Dialog openers ────────────────────────────────────────────────
    openAddDialog() {
      this.addForm = this.emptyAddForm();
      this.addStep = 1;
      this.dialogs.add = true;
    },
    openAllocateDialog(card) {
      this.selectedCard = card;
      this.allocateForm = {
        site: card.allocatedSite || null,
        chargerId: card.allocatedCharger || null,
        authMode: card.authMode || null,
      };
      this.dialogs.allocate = true;
    },
    openEditDialog(card) {
      this.selectedCard = card;
      this.addForm = { ...card };
      this.addStep = 1;
      this.dialogs.add = true;
    },
    openHistoryDialog(card) {
      this.selectedCard = card;
      this.dialogs.history = true;
    },
    openImportDialog() {
      this.dialogs.import = true;
    },

    // ── Wizard navigation ─────────────────────────────────────────────
    nextStep() {
      if (this.addStep === 1 && !this.$refs.addForm.validate()) return;
      this.addStep++;
    },

    // ── Save actions ──────────────────────────────────────────────────
    saveCard() {
      this.saving = true;
      setTimeout(() => {
        const existing = this.cards.find((c) => c.id === this.addForm.id);
        if (existing) {
          Object.assign(existing, { ...this.addForm });
        } else {
          this.cards.push({
            ...this.addForm,
            id: this.cards.length + 1,
            status: "Active",
            lastUsed: null,
            createdAt: new Date().toISOString().substr(0, 10),
          });
        }
        this.saving = false;
        this.dialogs.add = false;
        this.showSnack(
          "RFID card saved and settings applied successfully",
          "teal",
          "mdi-check-circle"
        );
      }, 800);
    },

    saveAllocation() {
      this.saving = true;
      setTimeout(() => {
        const card = this.cards.find((c) => c.id === this.selectedCard.id);
        if (card) {
          card.allocatedCharger = this.allocateForm.chargerId;
          card.allocatedSite = this.allocateForm.site;
          card.authMode = this.allocateForm.authMode;
        }
        this.saving = false;
        this.dialogs.allocate = false;
        this.showSnack(
          `Charger ${this.allocateForm.chargerId} allocated with ${this.allocateForm.authMode}`,
          "teal",
          "mdi-ev-station"
        );
      }, 600);
    },

    deallocateCard() {
      const card = this.cards.find((c) => c.id === this.selectedCard.id);
      if (card) {
        card.allocatedCharger = null;
        card.allocatedSite = null;
        card.authMode = null;
      }
      this.dialogs.allocate = false;
      this.showSnack("Charger allocation removed", "orange", "mdi-link-off");
    },

    toggleStatus(card) {
      const c = this.cards.find((x) => x.id === card.id);
      if (!c) return;
      if (c.status === "Active") {
        c.status = "Revoked";
        this.showSnack(`Card ${c.cardUid} revoked`, "red", "mdi-cancel");
      } else {
        c.status = "Active";
        this.showSnack(
          `Card ${c.cardUid} activated`,
          "green",
          "mdi-check-circle"
        );
      }
    },

    copyToClipboard(text) {
      navigator.clipboard?.writeText(text);
      this.showSnack(
        "UID copied to clipboard",
        "grey darken-2",
        "mdi-content-copy"
      );
    },

    handleCsvUpload(e) {
      const file = e.target.files[0];
      if (file)
        this.showSnack(
          `File "${file.name}" ready to import`,
          "teal",
          "mdi-upload"
        );
    },

    showSnack(message, color, icon) {
      this.snackbar = { show: true, message, color, icon };
    },
  },
};
</script>

<style scoped>
/* ─── PAGE ──────────────────────────────────────────────────────────────── */
.rfid-page {
  background: #f3f4f6;
  min-height: 100vh;
}

.rfid-page__title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}
.rfid-page__subtitle {
  font-size: 12px;
  color: #6b7280;
}
.rfid-page__wbs-badge {
  font-size: 10px !important;
}

/* ─── KPI CARDS ─────────────────────────────────────────────────────────── */
.rfid-kpi-card {
  border-radius: 10px !important;
}
.rfid-kpi-card__value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
}
.rfid-kpi-card__label {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

/* ─── TABLE ─────────────────────────────────────────────────────────────── */
.rfid-table {
  border-radius: 0 !important;
}
.rfid-table__uid {
  font-family: monospace;
  font-size: 12px;
}

/* ─── DIALOG HEADER ─────────────────────────────────────────────────────── */
.rfid-dialog__header {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  font-size: 16px !important;
  font-weight: 600;
  padding: 12px 16px !important;
}

/* ─── SECTION TITLE ─────────────────────────────────────────────────────── */
.rfid-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
}

/* ─── STEP INDICATOR ────────────────────────────────────────────────────── */
.rfid-steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.rfid-steps__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
}
.rfid-steps__circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  z-index: 1;
  transition: all 0.2s;
}
.rfid-steps__item--active .rfid-steps__circle {
  background: #00bfa5;
  color: white;
}
.rfid-steps__item--done .rfid-steps__circle {
  background: #22c55e;
  color: white;
}
.rfid-steps__label {
  font-size: 10px;
  color: #9ca3af;
  margin-top: 4px;
  text-align: center;
  white-space: nowrap;
}
.rfid-steps__item--active .rfid-steps__label,
.rfid-steps__item--done .rfid-steps__label {
  color: #00bfa5;
  font-weight: 600;
}
.rfid-steps__line {
  position: absolute;
  top: 14px;
  left: 55%;
  right: -55%;
  height: 2px;
  background: #e5e7eb;
  z-index: 0;
}
.rfid-steps__item--done .rfid-steps__line {
  background: #22c55e;
}

/* ─── AUTH MODE OPTION CARDS ────────────────────────────────────────────── */
.rfid-auth-option {
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.15s;
}
.rfid-auth-option:hover {
  border-color: #00bfa5;
  background: #f0fdfa;
}
.rfid-auth-option--selected {
  border-color: #00bfa5;
  background: #e6fafa;
}

/* ─── CHARGER PREVIEW ───────────────────────────────────────────────────── */
.rfid-charger-preview {
  background: #f0fdfa !important;
  border-color: #00bfa5 !important;
  border-radius: 8px !important;
}

/* ─── UPLOAD ZONE ───────────────────────────────────────────────────────── */
.rfid-upload-zone {
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
}
.rfid-upload-zone:hover {
  border-color: #00bfa5;
}

/* ─── HISTORY TABLE ─────────────────────────────────────────────────────── */
.rfid-history-table {
  border-radius: 0 !important;
}

.font-family-monospace {
  font-family: monospace;
  font-size: 11px;
}
.full-width {
  width: 100%;
}
</style>
